import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { API_ENDPOINTS } from '../config/api'
import SocialSignInButtons from './SocialSignInButtons'

export interface AuthSuccess {
  access_token: string
  session_token: string
  user: { id: string; email: string; full_name?: string | null }
}

interface Props {
  onSuccess: (data: AuthSuccess) => void
  /** Pre-fill the email (e.g. a guest who already gave it). */
  initialEmail?: string
  submitLabel?: string
  /** When set, Google/Apple sign-in attaches this anonymous response on return. */
  claimResponseId?: string | null
}

/**
 * Passwordless sign-in: enter email, receive a one-time code, enter it, done.
 * No password anywhere. Used by the sign-in page and the guest "save at the end" step.
 */
const PasswordlessSignIn: React.FC<Props> = ({ onSuccess, initialEmail = '', submitLabel, claimResponseId = null }) => {
  const intl = useIntl()
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState(initialEmail)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const sendLabel = submitLabel ?? intl.formatMessage({ id: 'auth.sendCode' })

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await fetch(API_ENDPOINTS.auth.requestOtp, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      if (res.status === 429) {
        setError(intl.formatMessage({ id: 'auth.otp.tooMany' }))
        return
      }
      if (!res.ok) {
        setError(intl.formatMessage({ id: 'auth.otp.couldNotSend' }))
        return
      }
      setStep('code')
    } catch {
      setError(intl.formatMessage({ id: 'auth.otp.networkError' }))
    } finally {
      setLoading(false)
    }
  }

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await fetch(API_ENDPOINTS.auth.verifyOtp, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      })
      if (!res.ok) {
        setError(intl.formatMessage({ id: 'auth.otp.invalidCode' }))
        return
      }
      const data: AuthSuccess = await res.json()
      onSuccess(data)
    } catch {
      setError(intl.formatMessage({ id: 'auth.otp.networkError' }))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      {step === 'email' && (
        <>
          <SocialSignInButtons claimResponseId={claimResponseId} />
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-surface px-2 text-foreground-muted"><FormattedMessage id="auth.orWithEmail" /></span></div>
          </div>
        </>
      )}
      {step === 'email' ? (
        <form onSubmit={requestCode} aria-label={intl.formatMessage({ id: 'auth.signInWithEmail' })} className="space-y-3">
          <label htmlFor="pl-email" className="block text-sm font-medium text-foreground"><FormattedMessage id="auth.emailLabel" /></label>
          <input
            id="pl-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full border border-border rounded-lg px-3 py-2"
          />
          <button
            type="submit"
            disabled={loading || !email}
            className="w-full bg-primary text-white font-semibold py-2.5 rounded-lg disabled:opacity-50"
          >
            {loading ? <FormattedMessage id="auth.sending" /> : sendLabel}
          </button>
        </form>
      ) : (
        <form onSubmit={verifyCode} aria-label={intl.formatMessage({ id: 'auth.enterCode' })} className="space-y-3">
          <p className="text-sm text-foreground-muted">
            <FormattedMessage id="auth.codeSentTo" values={{ email: <strong>{email}</strong> }} />
          </p>
          <label htmlFor="pl-code" className="block text-sm font-medium text-foreground"><FormattedMessage id="auth.codeLabel" /></label>
          <input
            id="pl-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            className="w-full border border-border rounded-lg px-3 py-2 tracking-widest text-center text-lg"
          />
          <button
            type="submit"
            disabled={loading || code.length < 6}
            className="w-full bg-primary text-white font-semibold py-2.5 rounded-lg disabled:opacity-50"
          >
            {loading ? <FormattedMessage id="auth.verifying" /> : <FormattedMessage id="auth.verifyContinue" />}
          </button>
          <button
            type="button"
            onClick={() => { setStep('email'); setCode(''); setError(null) }}
            className="w-full text-sm text-foreground-muted"
          >
            <FormattedMessage id="auth.useDifferentEmail" />
          </button>
        </form>
      )}
      {error && <p role="alert" className="text-sm text-error">{error}</p>}
    </div>
  )
}

export default PasswordlessSignIn
